import { MutableRefObject, RefObject, useEffect, useState } from 'react';

import { usePluralisationTranslation } from '@nocowanie/common-ui/hooks';

import { ACCOMMODATION_TILE } from '@app/consts';

const MOBILE_BREAKPOINT = 768;
const AMENITY_GAP = 8;

interface UseAmenitiesVisibilityOptions {
    listRef: RefObject<HTMLDivElement>;
    amenityItemsRef: MutableRefObject<(HTMLDivElement | null)[]>;
    overflowRef: RefObject<HTMLDivElement>;
    amenitiesCount: number;
}

/**
 * Custom hook to manage dynamic visibility of amenities based on container width.
 * Handles responsive display for mobile and desktop, showing an overflow count when
 * amenities don't fit. Returns `isInitialized` to drive a skeleton while calculating.
 */
export const useAmenitiesVisibility = ({
    listRef,
    amenityItemsRef,
    overflowRef,
    amenitiesCount,
}: UseAmenitiesVisibilityOptions) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [hiddenLabels, setHiddenLabels] = useState<string[]>([]);
    const [overflowText, setOverflowText] = useState('');

    const pluralise = usePluralisationTranslation('common', 'accommodationTile');

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const tileContainer = list.closest(
            '[data-testid="acc-tile-amenities"]',
        ) as HTMLElement | null;

        if (!tileContainer) return;

        const updateVisibility = () => {
            const overflowBox = overflowRef.current;
            const allAmenities = amenityItemsRef.current.filter(
                (el): el is HTMLDivElement => el !== null,
            );

            if (!overflowBox || !allAmenities.length) {
                return;
            }

            const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

            if (isMobile) {
                allAmenities.forEach((amenity, index) => {
                    amenity.style.display =
                        index < ACCOMMODATION_TILE.MAX_AMENITIES ? 'flex' : 'none';
                });
                overflowBox.style.display = 'none';
                setHiddenLabels([]);
                return;
            }

            const containerWidth = tileContainer.offsetWidth;
            const reservedSpace = overflowBox.offsetWidth + AMENITY_GAP;

            let currentWidth = 0;
            let visibleCount = 0;

            allAmenities.forEach(amenity => {
                amenity.style.display = 'flex';
            });

            for (let i = 0; i < allAmenities.length; i++) {
                const amenity = allAmenities[i];
                const amenityWidth = amenity.offsetWidth;
                const gap = visibleCount > 0 ? AMENITY_GAP : 0;
                const availableWidth =
                    i < allAmenities.length - 1 ? containerWidth - reservedSpace : containerWidth;

                if (currentWidth + amenityWidth + gap <= availableWidth) {
                    currentWidth += amenityWidth + gap;
                    visibleCount++;
                } else {
                    break;
                }
            }

            const newHiddenLabels: string[] = [];

            for (let i = visibleCount; i < allAmenities.length; i++) {
                const amenity = allAmenities[i];
                amenity.style.display = 'none';
                const label = amenity.getAttribute('data-amenity-label');
                if (label) newHiddenLabels.push(label);
            }

            if (newHiddenLabels.length > 0) {
                overflowBox.style.display = 'block';
                const count = newHiddenLabels.length;
                setHiddenLabels(newHiddenLabels);
                setOverflowText(pluralise(count, 'otherItems', `+${count}`));
            } else {
                overflowBox.style.display = 'none';
                setHiddenLabels([]);
                setOverflowText('');
            }
        };

        const observer = new ResizeObserver(() => updateVisibility());
        observer.observe(tileContainer);

        updateVisibility();
        setIsInitialized(true);

        return () => observer.disconnect();
    }, [amenitiesCount, pluralise, listRef, amenityItemsRef, overflowRef]);

    return { isInitialized, hiddenLabels, overflowText };
};
